import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router'
import { inject, observer } from 'mobx-react'
import { Formik, Form, Field } from 'formik'
import { toJS } from 'mobx'
import pick from 'lodash/pick'
import fromPairs from 'lodash/fromPairs'
import moment from 'moment'

import MonthYearPicker from '@components/MonthPicker/MonthYearPicker'
import WarningIcon from '@components/Icon/Warning'
import InfoBox from '@components/InfoBox'
import PhoneFields from '@components/Form/PhoneFields'
import EmailFields from '@components/Form/EmailFields'
import LanguageFields from '@components/Form/LanguageFields'
import Input from '@components/Form/Input'
import { Dropdown } from '@components/Form/Autocomplete'
import RichTextEditor from '@components/Form/RichTextEditor'
import OptionGroup from '@components/Form/OptionGroup'
import Button from '@components/Button'
import { employeeApi } from '@atheneum/api'
import getEmployeeValidationSchema from './validation'
import EmployeeFormCommon from './EmployeeForm.common'
import Tooltip from '@components/Tooltip'
import Checkbox from '@components/Form/Checkbox'

class EmployeeForm extends Component {
  static propTypes = {
    employee: PropTypes.object,
    modalStore: PropTypes.object,
    staticData: PropTypes.object,
    employeeStore: PropTypes.object,
    atheneumOfficeStore: PropTypes.object,
    history: PropTypes.object,
    userStore: PropTypes.object
  }

  handleTitleChange = (title, form) => {
    form.setFieldValue('title', title, false)
  }

  handleAmountChange = (amount, form) => {
    if (amount === '') {
      form.setFieldValue('currentPosition.hourlyRate', null, false)
    } else {
      form.setFieldValue('currentPosition.hourlyRate', Number(amount), false)
    }

    form.setFieldTouched('currentPosition.hourlyRate', true, true)
  }

  handlePositionChange = (positionId, form) => {
    const permissions = EmployeeFormCommon.getDefaultPermissionsByPosition(
      positionId,
      this.props.staticData
    )
    form.setFieldValue('currentPosition.employeePositionId', positionId, false)
    form.setFieldValue('permissions', permissions, false)
    form.setFieldTouched('currentPosition.employeePositionId', true, true)
    form.setFieldTouched('permissions', true, true)

    this.updateNewHireUntilDefault(positionId, form)
  }

  handleTargetMarginChange = (targetMargin, form) => {
    if (targetMargin === '') {
      form.setFieldValue('currentPosition.targetMargin', null, false)
    } else {
      form.setFieldValue(
        'currentPosition.targetMargin',
        Number(targetMargin),
        false
      )
    }

    form.setFieldTouched('currentPosition.targetMargin', true, true)
  }

  updateNewHireUntilDefault (positionId, form) {
    const {
      ASSOCIATE,
      ASSOCIATE_INTERN
    } = this.props.staticData.CONST.EMPLOYEE_POSITIONS
    const isAssociate = [ASSOCIATE, ASSOCIATE_INTERN].includes(positionId)

    if (this.props.employee) {
      return
    }

    form.setFieldValue(
      'employee.newHireUntil',
      (isAssociate
        ? moment().add(2, 'months')
        : moment().subtract(1, 'month')
      ).endOf('month'),
      false
    )
    form.setFieldTouched('employee.newHireUntil', true, true)
  }

  handleSubmit = (values, actions) => {
    const { employeeStore, employee, modalStore, history } = this.props
    this.saveEmployee(values)
      .then(savedEmployee => {
        employee
          ? employeeStore.get(savedEmployee.id)
          : history.push('/employee-search/employee/' + savedEmployee.id)
        modalStore.close()
      })
      .catch(({ message }) => {
        this.error = message
        actions.setSubmitting(false)
      })
  }

  saveEmployee = employeeData => {
    const { employee } = this.props

    employeeData.languages = employeeData.languages.filter(lang => lang.id)

    employeeData.address.phones = employeeData.address.phones.filter(
      phone => phone.phoneNum
    )

    employeeData.employee.newHireUntil = this.resolveMonthAndYear(
      employeeData.employee.newHireUntil
    ).format('YYYY-MM-DD')

    return employee ? employeeApi.update(employee.id, employeeData) : employeeApi.create(employeeData)
  }

  resolveMonthAndYear = newHireUntilValue => {
    const { employee } = this.props

    let date = moment().subtract(1, 'month')
    if (newHireUntilValue) {
      date = moment(newHireUntilValue)
    } else if (employee) {
      date = moment(employee.employee.created_at).subtract(2, 'month')
    }

    return date
  }

  render () {
    const { employee, modalStore, staticData, atheneumOfficeStore } = this.props

    const { titles, currencies, employeePositions, permissions } = staticData

    const { EMPLOYEE_POSITIONS, PERMISSIONS } = staticData.CONST
    const filteredPositions = employeePositions.filter(p => {
      return (
        this.props.userStore.isAdmin() ||
        [
          EMPLOYEE_POSITIONS.ASSOCIATE,
          EMPLOYEE_POSITIONS.ASSOCIATE_INTERN,
          EMPLOYEE_POSITIONS.TEAM_LEADER,
          EMPLOYEE_POSITIONS.ACCOUNT_MANAGER,
          EMPLOYEE_POSITIONS.PRINCIPAL
        ].includes(p.id) ||
        employee.currentPosition.employeePositionId === p.id
      )
    })
    const { atheneumOffices } = atheneumOfficeStore

    const employeePermissions = permissions.filter(perm => {
      const { PERMISSIONS } = staticData.CONST

      return ![
        PERMISSIONS.BASIC_INTERNAL,
        PERMISSIONS.BASIC_CLIENT,
        PERMISSIONS.BASIC_EXPERT
      ].includes(perm.id)
    })

    const permissionNames = fromPairs(
      employeePermissions.map(perm => [perm.id, perm.prettyName])
    )

    const permissionItems = employeePermissions.map(perm => perm.id)

    return (
      <div>
        <Formik
          enableReinitialize
          initialValues={initializeForm({ employee, staticData })}
          validationSchema={getEmployeeValidationSchema(employee)}
          onSubmit={this.handleSubmit}
          render={form => (
            <div className='expert-form'>
              <h1 className='expert-form__title'>
                {employee ? 'Edit employee' : 'Create employee'}
              </h1>
              {this.error ? (
                <InfoBox theme='red' icon={<WarningIcon />}>
                  {this.error}
                </InfoBox>
              ) : null}
              {this.warning ? (
                <InfoBox theme='yellow' icon={<WarningIcon />}>
                  {this.warning}
                </InfoBox>
              ) : null}
              <Form onSubmit={form.handleSubmit}>
                <div className='expert-form__section'>
                  <h2 className='expert-form__subtitle'>
                    Personal information
                  </h2>
                  <Field
                    name='title'
                    label='Title'
                    placeholder='Select title'
                    onChange={title => this.handleTitleChange(title, form)}
                    options={titles}
                    component={Dropdown}
                  />
                  <Field
                    name='firstName'
                    label='First name'
                    component={Input}
                  />
                  <Field name='lastName' label='Last name' component={Input} />
                </div>
                <div className='expert-form__section'>
                  <h2 className='expert-form__subtitle'>Contact information</h2>
                  <PhoneFields
                    form={form}
                    name='address.phones'
                    label='Phones'
                    phoneNotRepeating={form.errors.phoneNotRepeating}
                  />
                  <EmailFields
                    form={form}
                    primaryEmail='email'
                    additionalEmails='address.emails'
                    primaryEmailIsDisabled={!this.props.userStore.isAdmin()}
                    label='Emails'
                    emailNotRepeating={form.errors.emailNotRepeating}
                  />
                </div>
                <div className='expert-form__section'>
                  <h2 className='expert-form__subtitle'>
                    Professional profile
                  </h2>
                  <LanguageFields
                    form={form}
                    name='languages'
                    label='Languages'
                  />
                  <Field
                    name='employee.expertize'
                    label='Employee expertise'
                    toolbarRight
                    component={RichTextEditor}
                  />
                </div>
                <div className='expert-form__section'>
                  <h2 className='expert-form__subtitle'>Atheneum</h2>
                  <Field
                    name='employee.atheneumOfficeId'
                    label='Atheneum office'
                    placeholder='Select an office'
                    itemToString={item => item && item.name}
                    options={toJS(atheneumOffices)}
                    component={Dropdown}
                  />
                  <Field
                    name='currentPosition.employeePositionId'
                    label='Employee position'
                    placeholder='Select a position'
                    disabled={
                      !this.props.userStore.isAdmin() &&
                      this.props.employeeStore.employee.permissions.includes(
                        PERMISSIONS.ADMIN
                      )
                    }
                    itemToString={item => item && item.name}
                    options={filteredPositions}
                    onChange={position =>
                      this.handlePositionChange(position.id, form)
                    }
                    component={Dropdown}
                  />
                  <Field
                    name='currentPosition.positionDescription'
                    label='Position description'
                    component={Input}
                  />
                  <div className='expert-form__input-group'>
                    <label className='expert-form__label'>New hire until</label>
                    <div className='expert-form__input-wrapper expert-form__input-wrapper--two_one_children'>
                      <MonthYearPicker
                        monthAndYear={this.resolveMonthAndYear(
                          form.values.employee &&
                            form.values.employee.newHireUntil
                        )}
                        onChange={date => {
                          form.setFieldValue(
                            'employee.newHireUntil',
                            date.endOf('month'),
                            false
                          )
                          form.setFieldTouched(
                            'employee.newHireUntil',
                            true,
                            true
                          )
                        }}
                        value={form.values.employee.newHireUntil}
                        isMonthDisabled={(monthName, displayedYear) => {
                          const currentDate = moment(
                            `${monthName} ${displayedYear}`
                          )

                          return employee
                            ? moment(this.props.employee.created_at).subtract(
                                2,
                                'month'
                              ) > currentDate
                            : moment().subtract(2, 'month') > currentDate
                        }}
                        allowFutureMonthYear
                      />
                    </div>
                  </div>
                  <div className='expert-form__input-group'>
                    <label className='expert-form__label'>Hourly rate</label>
                    <div className='expert-form__input-wrapper expert-form__input-wrapper--two_one_children'>
                      <Field
                        name='currentPosition.currencyId'
                        placeholder='Currency'
                        itemToString={item => item && item.name}
                        options={currencies}
                        component={Dropdown}
                      />
                      <Field
                        name='currentPosition.hourlyRate'
                        placeholder='Amount'
                        onChange={e =>
                          this.handleAmountChange(e.target.value, form)
                        }
                        component={Input}
                      />
                    </div>
                  </div>
                  <Field
                    name='currentPosition.targetMargin'
                    label='Target Margin'
                    onChange={e =>
                      this.handleTargetMarginChange(e.target.value, form)
                    }
                    component={Input}
                  />
                </div>
                <div className='expert-form__section'>
                  <h2 className='expert-form__subtitle'>Permissions</h2>
                  <Field
                    name='permissions'
                    items={permissionItems}
                    itemToString={item => permissionNames[item]}
                    noStyle
                    disabled={!this.props.userStore.isAdmin()}
                    renderItem={EmployeeFormCommon.PermissionCheckbox}
                    component={OptionGroup}
                  />
                </div>
                <div className='expert-form__section'>
                  <h2 className='expert-form__subtitle'>Rydoo</h2>
                  <Field
                    name='employee.rydooFirstName'
                    label={
                      <div>
                        Rydoo First name
                        <Tooltip
                          placement='top'
                          tooltip='If set, this will set the value for the "First name" field on Rydoo.'
                        />
                      </div>
                    }
                    component={Input}
                  />
                  <Field
                    name='employee.rydooLastName'
                    label={
                      <div>
                        Rydoo Last name
                        <Tooltip
                          placement='top'
                          tooltip='If set, this will set the value for the "Last name" field on Rydoo.'
                        />
                      </div>
                    }
                    component={Input}
                  />
                  <Field
                    name='employee.rydooNickName'
                    label='Rydoo Nick Name'
                    component={Input}
                  />
                  <Field
                    name='employee.rydooPayrollEntityId'
                    label='Payroll entity'
                    placeholder='Select an office'
                    itemToString={item => item && item.name}
                    options={toJS(atheneumOffices)}
                    component={Dropdown}
                  />
                  <Field
                    name='employee.rydooGroupName'
                    label='Rydoo group name'
                    component={Input}
                  />
                  <Field
                    name='employee.rydooGroupId'
                    label='Rydoo group id'
                    component={Input}
                  />
                  <Field
                    name='employee.rydooSyncWithBamboo'
                    label='Automatically synchronize with Bamboo'
                    component={Checkbox}
                    className='employee-form-checkbox'
                  />
                </div>
                <div className='expert-form__section p-b-0'>
                  <Button
                    primary
                    submit
                    onClick={this.onSubmit}
                    disabled={form.isSubmitting}
                  >
                    Save
                  </Button>
                  <Button onClick={modalStore.close}>Cancel</Button>
                </div>
              </Form>
            </div>
          )}
        />
      </div>
    )
  }
}

EmployeeForm.defaultProps = {
  hideButtons: true,
  showCloseIcon: true,
  shouldCloseOnOverlayClick: false
}

export default withRouter(
  inject(
    'modalStore',
    'staticData',
    'growlStore',
    'userStore',
    'employeeStore',
    'atheneumOfficeStore'
  )(observer(EmployeeForm))
)

function initializeForm ({ employee, staticData }) {
  const initialValues = employee
    ? getInitialEmployeeValues(employee)
    : {
        title: '',
        firstName: '',
        lastName: '',
        address: {
          phones: [
            {
              phoneTypeId: staticData.CONST.PHONE_TYPES.OFFICE,
              phoneNum: ''
            }
          ],
          emails: []
        },
        email: '',
        employee: {
          atheneumOfficeId: null,
          rydooPayrollEntityId: null,
          expertize: '',
          newHireUntil: null,
          rydooSyncWithBamboo: true
        },
        currentPosition: {
          employeePositionId: null,
          positionDescription: '',
          hourlyRate: null,
          currencyId: null,
          targetMargin: null
        },
        languages: [
          {
            id: null,
            name: '',
            languageToUser: {
              languageId: null,
              languageProficiencyId: null
            }
          }
        ],
        permissions: []
      }
  return initialValues
}

function getInitialEmployeeValues (employee) {
  const initialEmployeeValues = pick(toJS(employee), [
    'title',
    'firstName',
    'lastName',
    'address',
    'email',
    'languages',
    'employee.atheneumOfficeId',
    'employee.expertize',
    'employee.rydooFirstName',
    'employee.rydooLastName',
    'employee.rydooNickName',
    'employee.rydooGroupName',
    'employee.rydooGroupId',
    'employee.rydooPayrollEntityId',
    'employee.rydooSyncWithBamboo',
    'permissions',
    'employee.newHireUntil'
  ])
  initialEmployeeValues.currentPosition = pick(toJS(employee.currentPosition), [
    'employeePositionId',
    'positionDescription',
    'hourlyRate',
    'currencyId',
    'targetMargin'
  ])

  if (!initialEmployeeValues.languages.length) {
    initialEmployeeValues.languages = [
      {
        id: null,
        name: '',
        languageToUser: {
          languageId: null,
          languageProficiencyId: null
        }
      }
    ]
  }
  return initialEmployeeValues
}
